import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { HtmlTooltip } from "components/HeatMap";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";
import Overlay from "components/Overlay/Overlay";

const LanguageNewEdit = () => {
  usePageTitle("Edit Language");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const {
    overlayData,
    setOverlayName,
    showSnackbar,
    handleErrorDialog,
    openDialog,
    closeDialog,
  } = useStateContext();

  const isEditMode = !!overlayData?.languageId;
  const allLanguages = useMemo(() => overlayData?.allLanguages || [], [overlayData?.allLanguages]);

  // Edit lock for existing languages only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "language",
    isEditMode ? overlayData?.languageId : null,
  );

  const [name, setName] = useState(overlayData?.languageName || "");
  const [initialName] = useState(overlayData?.languageName || "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteCheck, setDeleteCheck] = useState(null);
  const [loadingDeleteCheck, setLoadingDeleteCheck] = useState(false);

  const closeOverlay = () => setOverlayName("");

  const fetchDeleteCheck = useCallback(async () => {
    if (!overlayData?.languageId) return;

    setLoadingDeleteCheck(true);
    try {
      const response = await api.get(
        `/languages/${overlayData.languageId}/delete-check`,
      );
      setDeleteCheck(response.data);
    } catch (err) {
      console.error("Error fetching delete check:", err);
    } finally {
      setLoadingDeleteCheck(false);
    }
  }, [overlayData?.languageId]);

  // Fetch delete check data when in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchDeleteCheck();
    }
  }, [isEditMode, overlayData?.languageId, fetchDeleteCheck]);

  // Check if language name already exists (excluding current language if editing)
  const isDuplicate = useMemo(() => {
    const trimmedName = name.trim().toLowerCase();
    return allLanguages.some(
      (lang) =>
        lang.Name.toLowerCase() === trimmedName &&
        lang.id !== overlayData?.languageId,
    );
  }, [name, allLanguages, overlayData?.languageId]);

  const handleSave = async () => {
    if (!name.trim()) {
      showSnackbar("Language name is required", theme, "error");
      return;
    }

    if (isDuplicate) {
      showSnackbar("This language already exists", theme, "error");
      return;
    }

    const nameChanged = name.trim() !== initialName.trim();

    if (!nameChanged) {
      showSnackbar("No changes to save", theme, "info");
      return;
    }

    setSaving(true);

    try {
      if (isEditMode) {
        // Update existing language
        await api.put(`/languages/${overlayData.languageId}`, {
          Name: name.trim(),
        });
        showSnackbar("Language updated successfully", theme, "success");

        // Emit socket event for real-time updates
        if (socket) {
          socket.emit("settings:saved", {
            settingsType: "Language",
            recordId: overlayData.languageId,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      } else {
        // Create new language
        const response = await api.post("/languages", {
          Name: name.trim(),
        });
        showSnackbar("Language created successfully", theme, "success");

        // Emit socket event for real-time updates
        if (socket) {
          socket.emit("settings:saved", {
            settingsType: "Language",
            recordId: response.data.id,
            changeAction: "Created",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      }

      // Trigger callback to refresh languages list
      overlayData?.onLanguagesSaved?.();
      setSaving(false);
      closeOverlay();
    } catch (err) {
      console.error(err);
      handleErrorDialog(
        err.response?.data?.error || "Error saving language",
        theme,
      );
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!overlayData?.languageId) return;

    openDialog({
      theme: theme,
      title: "Delete Language",
      message: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        setDeleting(true);

        try {
          await api.delete(`/languages/${overlayData.languageId}`);
          showSnackbar("Language deleted successfully", theme, "success");

          // Emit socket event for real-time updates
          if (socket) {
            socket.emit("settings:saved", {
              settingsType: "Language",
              recordId: overlayData.languageId,
              changeAction: "Deleted",
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
            });
          }

          overlayData?.onLanguagesSaved?.();
          closeDialog();
          closeOverlay();
        } catch (err) {
          console.error(err);
          closeDialog();
          handleErrorDialog(
            err.response?.data?.error || "Error deleting language",
            theme,
          );
        } finally {
          setDeleting(false);
        }
      },
      secondaryOnClick: closeDialog,
    });
  };

  const handleDeleteClick = () => {
    if (!deleteCheck?.canDelete) {
      const reasons = [];
      if (deleteCheck?.linguistCount > 0) {
        reasons.push(`Linguist${deleteCheck.linguistCount > 1 ? "s" : ""}`);
      }
      if (deleteCheck?.serviceCount > 0) {
        reasons.push(`Service${deleteCheck.serviceCount > 1 ? "s" : ""}`);
      }
      showSnackbar(
        `Can't delete language being used by ${reasons.join(" and ")}`,
        theme,
        "error",
      );
      return;
    }
    handleDelete();
  };

  const renderDeleteTooltip = () => {
    if (!deleteCheck || deleteCheck.canDelete) return null;

    return (
      <Box>
        {deleteCheck.linguistCount > 0 && (
          <Box mb={1}>
            <Typography variant="subtitle2" fontWeight="bold">
              Linguists: {deleteCheck.linguistCount}
            </Typography>
            {deleteCheck.linguists.map((linguist) => (
              <Typography key={linguist.id} variant="body2">
                {linguist.name}
              </Typography>
            ))}
          </Box>
        )}
        {deleteCheck.serviceCount > 0 && (
          <Box>
            <Typography variant="subtitle2" fontWeight="bold">
              Services: {deleteCheck.serviceCount}
            </Typography>
            {deleteCheck.services.map((service) => (
              <Typography key={service.id} variant="body2">
                #{service.projectNumber}
              </Typography>
            ))}
            {deleteCheck.serviceCount > 10 && (
              <Typography
                variant="caption"
                color="text.secondary"
                fontStyle="italic"
              >
                ...and {deleteCheck.serviceCount - 10} more
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const pageTitle = isEditMode ? "Edit Language" : "New Language";

  const footerActions = (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: "100%", justifyContent: "space-between" }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {isEditMode && (
          <HtmlTooltip
            title={deleteCheck?.canDelete ? "" : renderDeleteTooltip()}
            arrow
            placement="bottom"
          >
            <span>
              {isMobile ? (
                <IconButton
                  color="error"
                  onClick={handleDeleteClick}
                  disabled={deleting || loadingDeleteCheck}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: `2px solid ${theme.palette.error.main}`,
                    borderRadius: "12px",
                    ...(!deleteCheck?.canDelete && {
                      opacity: 0.5,
                      borderColor: theme.palette.action.disabled,
                    }),
                  }}
                >
                  <DeleteRoundedIcon />
                </IconButton>
              ) : (
                <Button
                  loading={deleting}
                  color="error"
                  variant="outlined"
                  size="large"
                  endIcon={<DeleteRoundedIcon />}
                  onClick={handleDeleteClick}
                  disabled={deleting || loadingDeleteCheck}
                  sx={{
                    borderRadius: "12px",
                    ...(!deleteCheck?.canDelete && {
                      opacity: 0.5,
                      color: theme.palette.action.disabled,
                      borderColor: theme.palette.action.disabled,
                    }),
                  }}
                >
                  Delete
                </Button>
              )}
            </span>
          </HtmlTooltip>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {isMobile ? (
          <Tooltip title="Discard" placement="top">
            <IconButton
              color="primary"
              onClick={closeOverlay}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
              }}
            >
              <DisabledByDefaultRoundedIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            color="primary"
            variant="outlined"
            size="large"
            onClick={closeOverlay}
            sx={{ borderRadius: "12px" }}
          >
            Discard
          </Button>
        )}

        <Tooltip
          title={
            isMobile && (saving || isDuplicate)
              ? "Please fix errors before saving"
              : ""
          }
          placement="top"
        >
          <span>
            {isMobile ? (
              <IconButton
                color="primary"
                onClick={handleSave}
                disabled={saving || isDuplicate}
                sx={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.background.default,
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                <SaveRoundedIcon />
              </IconButton>
            ) : (
              <Button
                loading={saving}
                color="primary"
                variant="contained"
                size="large"
                endIcon={<SaveRoundedIcon />}
                onClick={handleSave}
                disabled={saving || isDuplicate}
                sx={{ borderRadius: "12px" }}
              >
                Save
              </Button>
            )}
          </span>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      <Overlay
        id="language-new-edit"
        open={true}
        onClose={closeOverlay}
        title={pageTitle}
        height="fit-content"
        footer={footerActions}
      >
        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            width: "100%",
            overflowY: "auto",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Language Details
              </Typography>
            </Grid>

            {isDuplicate && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="error">
                  This language name already exists in the system.
                </Alert>
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Language Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                inputProps={{ maxLength: 100 }}
                placeholder="e.g., Spanish, Mandarin Chinese, French"
                error={isDuplicate}
              />
            </Grid>
          </Grid>
        </Box>
      </Overlay>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </>
  );
};

export default LanguageNewEdit;
